"use client";

import { useEffect } from "react";
import { useHintContext, type Hint } from "@/contexts/hint-context";

/**
 * Publish a hint into the shared HintContext while the caller is mounted.
 * Pass `null` to publish nothing (e.g. behind an `enabled` guard); the hook
 * still cleans up any previously-published entry from this source.
 *
 * Each producer owns one slot keyed by `hint.source`. Memoize the hint
 * object in the caller (useMemo) so the publish only fires when the hint
 * actually changes.
 */
export function useHintPublisher(hint: Hint | null): void {
  const { publish, retract } = useHintContext();

  useEffect(() => {
    if (!hint) {
      return;
    }

    publish(hint);

    return () => retract(hint.source);
  }, [hint, publish, retract]);
}
